import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { utc } from '@date-fns/utc'
import {
    Card,
    CardContent,
    Box,
    TextField,
    Autocomplete,
    Tabs,
    Tab,
} from '@mui/material'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { addMonths, formatISO, startOfMonth } from 'date-fns'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod/v4-mini'
import { I18NAwareLocalizationProvider } from '@/components/I18NAwareLocalizationProvider'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { renderProjectGroup } from '@/features/admin/helper/projectAutocomplete'
import { CompareHoursToWorkingTimeModelGraph } from '@/features/admin/statistics/components/CompareHoursToWorkingTimeModelGraph'
import { GraphsLayout } from '@/features/admin/statistics/components/GraphsLayout'
import { HoursInSelectionGraph } from '@/features/admin/statistics/components/HoursInSelectionGraph'
import { ProjectHoursGraph } from '@/features/admin/statistics/components/ProjectHoursGraph'
import { StatisticsPendingComponent } from '@/features/admin/statistics/components/StatisticsPendingComponent'
import {
    StatisticsTabSchema,
    statisticsTabSchema,
} from '@/features/admin/statistics/schema/statisticsTabSchema'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { IdentityUserSortBy } from '@/generated/Enumerations/IdentityUserSortBy_pb'
import { ProjectSortBy } from '@/generated/Enumerations/ProjectSortBy_pb'
import { parseId, stringifyId } from '@/helper/id'
import { useAppForm } from '@/hooks/useAppForm'
import { projectsQueryOptions, useProjects } from '@/queries/useProjects'
import { usersQueryOptions, useUsers } from '@/queries/useUsers'
import {
    searchParamsPaginationDefaultValues,
    searchParamsPaginationSchema,
} from '@/schemas/searchParams/searchParamsPaginationSchema'
import {
    searchParamsProjectIds,
    searchParamsProjectIdsDefaultValue,
} from '@/schemas/searchParams/searchParamsProjectIds'
import { searchParamsToFromSchema } from '@/schemas/searchParams/searchParamsToFromSchema'

const today = new Date()

const getStart = (date: string | null | undefined) =>
    date === undefined || date === null ? startOfMonth(today, { in: utc })
    : typeof date === 'string' ? new Date(date)
    : date

const getEnd = (date: string | null | undefined) =>
    date === undefined || date === null ?
        startOfMonth(addMonths(today, 1), { in: utc })
    : typeof date === 'string' ? new Date(date)
    : date

const RouteComponent: React.FC = () => {
    const { t } = useTranslation()

    const { from, to, projectIds, userIds, tab, page } = Route.useSearch()
    const parsedDateFrom = React.useMemo(() => getStart(from), [from])
    const parsedDateTo = React.useMemo(() => getEnd(to), [to])

    const form = useAppForm({
        defaultValues: {
            dateFrom: parsedDateFrom,
            dateTo: parsedDateTo,
            projectIds,
            userIds,
        },
        onSubmit: ({ value: { dateFrom, dateTo, projectIds, userIds } }) =>
            navigate({
                search: prev => ({
                    ...prev,
                    from: formatISO(dateFrom, {
                        representation: 'date',
                    }),
                    to: formatISO(dateTo, {
                        representation: 'date',
                    }),
                    projectIds,
                    userIds,
                }),
                replace: true,
            }),

        listeners: {
            onChange: ({ formApi }) => formApi.handleSubmit(),
            onChangeDebounceMs: 1000,
        },
    })

    const navigate = Route.useNavigate()

    const {
        data: { projects },
    } = useProjects(
        {
            deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
            sorting: [{ sortBy: ProjectSortBy.NAME }],
            dateFrom: timestampFromDate(parsedDateFrom),
            dateTo: timestampFromDate(parsedDateTo),
        },
        true
    )
    const {
        data: { users },
    } = useUsers(
        {
            deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
            sorting: [
                { sortBy: IdentityUserSortBy.LAST_NAME },
                { sortBy: IdentityUserSortBy.FIRST_NAME },
            ],
        },
        true
    )

    const usersToLoadTrackingsFor =
        userIds.length === 0 ?
            users
        :   users.filter(p => userIds.includes(stringifyId(p.id)))

    const projectsColorMap = new Map(
        projects.flatMap(p =>
            p.id && p.displayColor ? [[stringifyId(p.id), p.displayColor]] : []
        )
    )

    return (
        <Card>
            <title>{t('common.statistics')}</title>

            <CardContent>
                <form.AppForm>
                    <form>
                        <Box
                            sx={{
                                my: 2,
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <I18NAwareLocalizationProvider>
                                <form.AppField name={'dateFrom'}>
                                    {field => (
                                        <field.DatePicker
                                            label={t('common.from')}
                                            sx={{ flex: 1, minWidth: 200 }}
                                        />
                                    )}
                                </form.AppField>

                                <form.AppField name={'dateTo'}>
                                    {field => (
                                        <field.DatePicker
                                            label={t('common.to')}
                                            sx={{ flex: 1, minWidth: 200 }}
                                        />
                                    )}
                                </form.AppField>
                            </I18NAwareLocalizationProvider>
                        </Box>

                        <Box
                            sx={{
                                my: 2,
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <form.AppField name={'projectIds'}>
                                {field => (
                                    <Autocomplete
                                        multiple={true}
                                        options={projects}
                                        getOptionKey={p => stringifyId(p.id)}
                                        getOptionLabel={p => p.name}
                                        // Group all project names by the beginning of its name before the first '.'.
                                        // f.e. 'ABC.DEF.GHJ' is assigned to the group 'ABC'
                                        groupBy={p =>
                                            p.name.split('.')[0] ?? ''
                                        }
                                        renderGroup={renderProjectGroup(
                                            groupName => async () => {
                                                const prevSet = new Set(
                                                    projectIds
                                                )
                                                const groupMembersSet = new Set(
                                                    projects.flatMap(p =>
                                                        (
                                                            p.name.startsWith(
                                                                groupName
                                                            )
                                                        ) ?
                                                            [stringifyId(p.id)]
                                                        :   []
                                                    )
                                                )
                                                // calculate the overlap between the new and previous data
                                                const intersection =
                                                    prevSet.intersection(
                                                        groupMembersSet
                                                    )

                                                // if the intersection covers all group members, remove them from the state

                                                await navigate({
                                                    search: prev => ({
                                                        ...prev,
                                                        projectIds:
                                                            (
                                                                intersection.size ===
                                                                groupMembersSet.size
                                                            ) ?
                                                                projectIds.filter(
                                                                    id =>
                                                                        !groupMembersSet.has(
                                                                            id
                                                                        )
                                                                )
                                                                // if no intersection exists, just add them to the state
                                                                // or if the intersection doesn't cover all group members, add the missing to the state
                                                            :   [
                                                                    ...prevSet.union(
                                                                        groupMembersSet
                                                                    ),
                                                                ],
                                                    }),
                                                    replace: true,
                                                })
                                            }
                                        )}
                                        value={projects.filter(p =>
                                            field.state.value.includes(
                                                stringifyId(p.id)
                                            )
                                        )}
                                        isOptionEqualToValue={(a, b) =>
                                            stringifyId(a.id) ===
                                            stringifyId(b.id)
                                        }
                                        onChange={(_, value) =>
                                            field.handleChange(
                                                value.map(p =>
                                                    stringifyId(p.id)
                                                )
                                            )
                                        }
                                        renderInput={params => (
                                            <TextField
                                                label={t('common.projects')}
                                                {...params}
                                            />
                                        )}
                                        style={{ flex: 1, minWidth: 200 }}
                                        disableCloseOnSelect={true}
                                        onBlur={field.handleBlur}
                                    />
                                )}
                            </form.AppField>

                            <form.AppField name={'userIds'}>
                                {field => (
                                    <Autocomplete
                                        multiple={true}
                                        options={users}
                                        getOptionKey={p => stringifyId(p.id)}
                                        getOptionLabel={p =>
                                            `${p.lastName} ${p.firstName}`
                                        }
                                        value={users.filter(p =>
                                            field.state.value.includes(
                                                stringifyId(p.id)
                                            )
                                        )}
                                        isOptionEqualToValue={(a, b) =>
                                            stringifyId(a.id) ===
                                            stringifyId(b.id)
                                        }
                                        onChange={(_, value) =>
                                            field.handleChange(
                                                value.map(p =>
                                                    stringifyId(p.id)
                                                )
                                            )
                                        }
                                        renderInput={params => (
                                            <TextField
                                                label={t('common.user')}
                                                {...params}
                                            />
                                        )}
                                        onBlur={field.handleBlur}
                                        disableCloseOnSelect={true}
                                        style={{ flex: 1, minWidth: 200 }}
                                    />
                                )}
                            </form.AppField>
                        </Box>
                    </form>
                </form.AppForm>

                <Tabs
                    value={tab}
                    onChange={(_, value: StatisticsTabSchema) =>
                        navigate({
                            search: prev => ({ ...prev, tab: value, page: 0 }),
                        })
                    }
                >
                    <Tab
                        value={'project_hours' as StatisticsTabSchema}
                        label={t('statistics.hoursPerProject')}
                    />
                    <Tab
                        value={'hours_in_selection' as StatisticsTabSchema}
                        label={t('statistics.hoursInTimespan')}
                    />
                    <Tab
                        value={
                            'compare_worked_hours_to_wtm' as StatisticsTabSchema
                        }
                        label={t('statistics.compareHoursToWorkingTimeModel')}
                    />
                </Tabs>

                {tab === 'project_hours' && (
                    <GraphsLayout
                        Graph={ProjectHoursGraph}
                        page={page}
                        users={usersToLoadTrackingsFor}
                        GraphProps={{
                            parsedDateFrom,
                            parsedDateTo,
                            projectIds,
                            projectsColorMap,
                        }}
                    />
                )}

                {tab === 'hours_in_selection' && (
                    <GraphsLayout
                        Graph={HoursInSelectionGraph}
                        page={page}
                        users={usersToLoadTrackingsFor}
                        GraphProps={{
                            parsedDateFrom,
                            parsedDateTo,
                            projectIds,
                            projectsColorMap,
                        }}
                    />
                )}

                {tab === 'compare_worked_hours_to_wtm' && (
                    <GraphsLayout
                        Graph={CompareHoursToWorkingTimeModelGraph}
                        page={page}
                        users={usersToLoadTrackingsFor}
                        GraphProps={{
                            parsedDateFrom,
                            parsedDateTo,
                            projectIds,
                            projectsColorMap,
                        }}
                    />
                )}
            </CardContent>
        </Card>
    )
}

const stringArrayDefaultValue: string[] = []

export const Route = createFileRoute('/__base-layout/admin/statistics')({
    component: RouteComponent,
    pendingMs: 2000,
    pendingComponent: StatisticsPendingComponent,

    validateSearch: z.object({
        ...searchParamsToFromSchema.shape,
        ...searchParamsProjectIds.shape,
        ...searchParamsPaginationSchema.shape,

        userIds: z._default(z.array(z.string()), stringArrayDefaultValue),
        tab: z._default(statisticsTabSchema, 'project_hours'),
    }),
    search: {
        middlewares: [
            stripSearchParams({
                ...searchParamsProjectIdsDefaultValue,
                ...searchParamsPaginationDefaultValues,

                userIds: stringArrayDefaultValue,
                tab: 'project_hours',
            }),
        ],
    },

    beforeLoad: async ({
        context: { queryClient, grpcClients, user },
        search: { userIds },
    }) => {
        assertUserIsAdminOrRedirect(user)

        await Promise.all([
            queryClient.ensureQueryData(
                usersQueryOptions(
                    grpcClients,
                    {
                        ids: userIds.map(parseId),
                        deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
                        sorting: [
                            { sortBy: IdentityUserSortBy.LAST_NAME },
                            { sortBy: IdentityUserSortBy.FIRST_NAME },
                        ],
                    },
                    true
                )
            ),
            queryClient.ensureQueryData(
                projectsQueryOptions(grpcClients, {
                    deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
                    sorting: [{ sortBy: ProjectSortBy.NAME }],
                })
            ),
        ])
    },
})
